// SAD-493 (TEAM-01): our position on this Pursuit and gap-driven teaming
// candidates. A candidate is a canonical Company proposed against specific
// solution gaps with a human-entered rationale. Nothing here contacts the
// company or records an agreement; status is a note-backed label.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraEmptyState, SpecraOverlayDialog, SpecraOverlayDrawer, SpecraSectionHeader, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CaptureTeamingCandidate, GetCaptureTeamingPositionResponse } from '../../../../../gen/ts/mono/projectdata/v1/capture_teaming_candidates_pb';
import { SharedCompanyPicker } from '../../components/SharedRecord';
import { projectDataClient } from '../../lib/connect/clients';
import { useTenantId } from '../Delivery/_shared/hooks/useTenantId';
import { capabilityHooks } from '../Delivery/Registries/hooks';
import { TextField } from './PastPerformance/Fields';
import { pursuitSectionPath } from './record/pursuitSections';
import { ReadOnlyScope } from './record/ReadOnlyScope';
import { RowActionsMenu } from './record/RowActionsMenu';
const T = Testids.captureTeaming;
const label = (v)=>v.replace(/_/g, ' ');
const when = (d)=>d ? d.toDate().toISOString().slice(0, 10) : '';
function CandidateRow({ captureProjectId, cand, gapText, note, onStatus, readOnly = false }) {
    const queryClient = useQueryClient();
    const current = cand.currentAssessment ?? cand.assessmentHistory?.[0];
    const assessments = [
        ...current ? [
            current
        ] : [],
        ...(cand.assessmentHistory ?? []).filter((a)=>a.id !== current?.id)
    ].sort((a, b)=>b.version - a.version || a.id.localeCompare(b.id));
    const [draft, setDraft] = useState();
    const [saving, setSaving] = useState(false);
    const [revisionError, setRevisionError] = useState('');
    const busy = useRef(false);
    const retry = useRef();
    function closeEditor() {
        if (busy.current) return;
        setDraft(undefined);
        setRevisionError('');
        retry.current = undefined;
    }
    async function saveAssessment() {
        if (!draft || busy.current || !draft.rationale.trim() || draft.gapIds.length === 0) return;
        busy.current = true;
        setSaving(true);
        setRevisionError('');
        const replacement = {
            captureProjectId,
            candidateId: cand.id,
            companyProfileId: cand.companyProfileId,
            ...draft,
            evidenceRefs: draft.evidenceRefs.split('\n').filter((ref)=>ref.trim())
        };
        const payload = JSON.stringify(replacement);
        if (retry.current?.payload !== payload) {
            retry.current = {
                payload,
                requestId: crypto.randomUUID()
            };
        }
        try {
            const { assessment } = await projectDataClient.reviseCaptureTeamingAssessment({
                ...replacement,
                requestId: retry.current.requestId
            });
            if (!assessment) {
                throw new Error('The server did not return the saved assessment. Retry Save assessment.');
            }
            queryClient.setQueryData([
                'capture-teaming-position',
                captureProjectId
            ], (data)=>data && new GetCaptureTeamingPositionResponse({
                    ...data,
                    candidates: data.candidates.map((candidate)=>candidate.id !== cand.id ? candidate : new CaptureTeamingCandidate({
                            ...candidate,
                            rationale: assessment.rationale,
                            theirCapability: assessment.theirCapability,
                            gapIds: assessment.gapIds,
                            currentAssessment: assessment,
                            assessmentHistory: [
                                assessment,
                                ...assessments.filter((a)=>a.id !== assessment.id)
                            ]
                        }))
                }));
            setDraft(undefined);
            retry.current = undefined;
            void queryClient.invalidateQueries({
                queryKey: [
                    'capture-teaming-position',
                    captureProjectId
                ]
            });
        } catch (e) {
            const error = ConnectError.from(e);
            setRevisionError(error.rawMessage + (error.code === Code.AlreadyExists ? ' Refresh and review the latest assessment before editing again; your draft is retained here.' : ''));
        } finally{
            busy.current = false;
            setSaving(false);
        }
    }
    const buttons = [
        [
            'under_review',
            'Mark under review',
            T.candReview
        ],
        [
            'shortlisted',
            'Shortlist',
            T.candShortlist
        ],
        [
            'selected',
            'Select as partner',
            T.candSelect
        ],
        [
            'declined',
            'Decline',
            T.candDecline
        ]
    ];
    return /*#__PURE__*/ _jsxs("li", {
        "data-testid": T.candRow,
        className: "space-y-1 rounded-md border p-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 text-sm",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: cand.companyName || cand.companyProfileId
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        "data-testid": T.candStatus,
                        children: /*#__PURE__*/ _jsx(SpecraBadge, {
                            children: label(cand.status)
                        })
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "ml-auto",
                        children: /*#__PURE__*/ _jsx(RowActionsMenu, {
                            rowName: cand.companyName || cand.companyProfileId,
                            readOnly: readOnly,
                            onEdit: {
                                label: 'Edit assessment',
                                testId: T.assessmentEdit,
                                disabled: !current,
                                onSelect: ()=>{
                                    if (!current) return;
                                    setRevisionError('');
                                    retry.current = undefined;
                                    setDraft({
                                        predecessorId: current.id,
                                        rationale: current.rationale,
                                        theirCapability: current.theirCapability,
                                        gapIds: [
                                            ...current.gapIds
                                        ],
                                        evidenceRefs: current.evidenceRefs.join('\n'),
                                        reason: ''
                                    });
                                }
                            }
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": T.assessmentCurrent,
                className: "whitespace-pre-wrap break-words text-xs",
                children: [
                    "Rationale: ",
                    current?.rationale ?? cand.rationale,
                    (current?.theirCapability ?? cand.theirCapability) && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            "Brings: ",
                            current?.theirCapability ?? cand.theirCapability
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 pl-3 text-xs",
                children: (current?.gapIds ?? cand.gapIds).map((id)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": T.candGapLink,
                        children: [
                            "Proposed against gap: ",
                            gapText.get(id) ?? id
                        ]
                    }, id))
            }),
            /*#__PURE__*/ _jsxs("section", {
                "aria-label": "Assessment history",
                "data-testid": T.assessmentHistory,
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "font-semibold",
                        children: "Assessment history"
                    }),
                    assessments.map((assessment)=>/*#__PURE__*/ _jsxs("fieldset", {
                            "aria-label": `Assessment version ${assessment.version}`,
                            "data-testid": T.assessmentVersion,
                            className: "space-y-1 whitespace-pre-wrap break-words rounded-md border p-2 text-xs",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        "Version ",
                                        assessment.version,
                                        assessment.id === current?.id ? ' (current)' : ''
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        "Rationale: ",
                                        assessment.rationale
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        "Brings: ",
                                        assessment.theirCapability || 'None recorded'
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    children: assessment.gapIds.map((id)=>/*#__PURE__*/ _jsxs("li", {
                                            children: [
                                                "Gap: ",
                                                gapText.get(id) ?? 'Description unavailable',
                                                " (",
                                                id,
                                                ")"
                                            ]
                                        }, id))
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: "Supporting evidence references:"
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    children: assessment.evidenceRefs.map((ref, index)=>/*#__PURE__*/ _jsx("li", {
                                            children: ref
                                        }, `${index}-${ref}`))
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        "By ",
                                        assessment.actorUserId,
                                        " on ",
                                        assessment.createdAt?.toDate().toISOString()
                                    ]
                                }),
                                assessment.reason && /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        "Revision reason: ",
                                        assessment.reason
                                    ]
                                })
                            ]
                        }, assessment.id))
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraOverlayDrawer, {
                open: !!draft,
                onOpenChange: (open)=>{
                    if (!open) closeEditor();
                },
                title: "Edit assessment",
                description: "Saves a new version; earlier ones stay in the history.",
                testId: T.assessmentDialog,
                width: "lg",
                bodyProps: {
                    className: 'space-y-3',
                    'aria-busy': saving
                },
                footer: /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-2 p-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": T.assessmentCancel,
                            disabled: saving,
                            onClick: closeEditor,
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": T.assessmentSave,
                            disabled: saving || !draft?.rationale.trim() || !draft.gapIds.length,
                            isLoading: saving,
                            onClick: saveAssessment,
                            children: "Save assessment"
                        })
                    ]
                }),
                children: [
                    draft && /*#__PURE__*/ _jsxs("fieldset", {
                        disabled: saving,
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx("legend", {
                                className: "sr-only",
                                children: "Assessment details"
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "Rationale",
                                required: true,
                                multiline: true,
                                testId: T.assessmentRationale,
                                value: draft.rationale,
                                onChange: (rationale)=>setDraft({
                                        ...draft,
                                        rationale
                                    })
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "What they bring",
                                multiline: true,
                                testId: T.assessmentCapability,
                                value: draft.theirCapability,
                                onChange: (theirCapability)=>setDraft({
                                        ...draft,
                                        theirCapability
                                    })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        children: "Gaps (choose at least one)"
                                    }),
                                    [
                                        ...new Set([
                                            ...gapText.keys(),
                                            ...draft.gapIds
                                        ])
                                    ].map((id)=>/*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                            label: `${gapText.get(id) ?? 'Description unavailable'} (${id})`,
                                            controlProps: {
                                                ...{
                                                    'data-testid': T.assessmentGapOption
                                                },
                                                checked: draft.gapIds.includes(id),
                                                disabled: saving,
                                                onCheckedChange: (checked)=>setDraft({
                                                        ...draft,
                                                        gapIds: checked === true ? [
                                                            ...draft.gapIds,
                                                            id
                                                        ] : draft.gapIds.filter((gapId)=>gapId !== id)
                                                    })
                                            }
                                        }, id))
                                ]
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "Supporting evidence",
                                hint: "One reference per line.",
                                multiline: true,
                                testId: T.assessmentEvidenceRefs,
                                value: draft.evidenceRefs,
                                onChange: (evidenceRefs)=>setDraft({
                                        ...draft,
                                        evidenceRefs
                                    })
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "Reason for this revision",
                                multiline: true,
                                testId: T.assessmentReason,
                                value: draft.reason,
                                onChange: (reason)=>setDraft({
                                        ...draft,
                                        reason
                                    })
                            })
                        ]
                    }),
                    revisionError && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        "data-testid": T.assessmentError,
                        children: revisionError
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 pl-3 text-xs",
                children: cand.history.map((h)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": T.candHistory,
                        children: [
                            label(h.status),
                            " on ",
                            when(h.at),
                            ": ",
                            h.note
                        ]
                    }, h.id))
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex gap-2",
                children: buttons.map(([status, text, testId])=>/*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": testId,
                        disabled: !note.trim() || cand.status === status,
                        onClick: ()=>onStatus(cand, status),
                        children: text
                    }, status))
            })
        ]
    });
}
export function TeamingCandidates({ captureProjectId, readOnly = false }) {
    const queryClient = useQueryClient();
    const tenantId = useTenantId();
    const capabilities = capabilityHooks.useList({
        companyId: tenantId
    });
    const [cap, setCap] = useState({
        statement: '',
        capabilityId: '',
        evidenceLabel: '',
        evidenceRef: ''
    });
    const [companyId, setCompanyId] = useState('');
    const [rationale, setRationale] = useState('');
    const [theirCapability, setTheirCapability] = useState('');
    const [gapIds, setGapIds] = useState([]);
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    // GRW-39: the forms open from Add buttons; one dialog at a time.
    const [dialog, setDialog] = useState();
    const position = useQuery({
        queryKey: [
            'capture-teaming-position',
            captureProjectId
        ],
        queryFn: ()=>projectDataClient.getCaptureTeamingPosition({
                captureProjectId
            }),
        retry: false
    });
    const solution = useQuery({
        queryKey: [
            'capture-solution-alternatives',
            captureProjectId
        ],
        queryFn: ()=>projectDataClient.getCaptureSolutionAlternatives({
                captureProjectId
            }),
        retry: false
    });
    async function run(fn) {
        setError('');
        try {
            await fn();
            setDialog(undefined);
            await queryClient.invalidateQueries({
                queryKey: [
                    'capture-teaming-position',
                    captureProjectId
                ]
            });
        } catch (e) {
            setError(ConnectError.from(e).rawMessage);
        }
    }
    const openDialog = (next)=>{
        setError('');
        setDialog(next);
    };
    const errorLine = error && /*#__PURE__*/ _jsx("p", {
        role: "alert",
        "data-testid": T.error,
        children: error
    });
    if (position.isLoading) return /*#__PURE__*/ _jsx("p", {
        children: "Loading our position…"
    });
    if (position.isError && !position.data) {
        return /*#__PURE__*/ _jsx("p", {
            role: "alert",
            children: ConnectError.from(position.error).rawMessage
        });
    }
    const gaps = (solution.data?.gaps ?? []).filter((g)=>g.status === 'open');
    const gapText = new Map((solution.data?.gaps ?? []).map((g)=>[
            g.id,
            g.description
        ]));
    const hasCapabilities = (position.data?.capabilities ?? []).length > 0;
    const hasCandidates = (position.data?.candidates ?? []).length > 0;
    const registry = new Map((capabilities.data?.items ?? []).map((c)=>[
            c.id,
            c.name
        ]));
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsxs("section", {
            "data-testid": T.root,
            className: "space-y-3 rounded-md border p-3",
            children: [
                position.isError && /*#__PURE__*/ _jsxs("p", {
                    role: "alert",
                    children: [
                        "Could not refresh assessments: ",
                        ConnectError.from(position.error).rawMessage
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                    title: "Our position",
                    variant: "group",
                    action: !readOnly && hasCapabilities ? /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>openDialog('capability'),
                        "data-testid": Testids.pursuitTeaming.capabilityAddButton,
                        children: "Add capability"
                    }) : undefined
                }),
                /*#__PURE__*/ _jsx("p", {
                    "data-testid": T.noOutreach,
                    className: "text-sm",
                    children: "Our capabilities and open gaps for this Pursuit, with the evidence behind each. Proposing a teaming candidate is a recorded judgement only: it sends nothing to the company and records no agreement."
                }),
                hasCapabilities ? /*#__PURE__*/ _jsx("ul", {
                    className: "space-y-2",
                    children: (position.data?.capabilities ?? []).map((c)=>/*#__PURE__*/ _jsxs("li", {
                            "data-testid": T.capRow,
                            className: "rounded-md border p-2 text-sm",
                            children: [
                                c.statement,
                                c.capabilityId && ` [${registry.get(c.capabilityId) ?? c.capabilityId}]`,
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "text-xs",
                                    children: [
                                        "Evidence: ",
                                        c.evidenceLabel,
                                        c.evidenceRef && ` (${c.evidenceRef})`
                                    ]
                                })
                            ]
                        }, c.id))
                }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    size: "compact",
                    title: "No capabilities recorded yet.",
                    description: "Add what we bring to this pursuit, with the evidence behind it.",
                    "data-testid": Testids.pursuitTeaming.capabilityEmptyState,
                    action: readOnly ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>openDialog('capability'),
                        "data-testid": Testids.pursuitTeaming.capabilityAddButton,
                        children: "Add capability"
                    })
                }),
                !readOnly && /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                    open: dialog === 'capability',
                    onOpenChange: (open)=>setDialog(open ? 'capability' : undefined),
                    title: "Add our capability",
                    size: "lg",
                    testId: Testids.pursuitTeaming.capabilityDialog,
                    footerProps: {
                        onCancel: ()=>setDialog(undefined),
                        confirmLabel: 'Add our capability',
                        onConfirm: ()=>run(async ()=>{
                                await projectDataClient.addCapturePositionCapability({
                                    captureProjectId,
                                    requestId: crypto.randomUUID(),
                                    capability: cap
                                });
                                setCap({
                                    statement: '',
                                    capabilityId: '',
                                    evidenceLabel: '',
                                    evidenceRef: ''
                                });
                            }),
                        confirmProps: {
                            disabled: !cap.statement.trim() || !cap.evidenceLabel.trim(),
                            'data-testid': T.capAdd
                        }
                    },
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "Our capability",
                                testId: T.capStatement,
                                value: cap.statement,
                                onChange: (statement)=>setCap((c)=>({
                                            ...c,
                                            statement
                                        }))
                            }),
                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                "aria-label": "Registry capability (optional)",
                                "data-testid": T.capRegistry,
                                className: "w-full rounded-md border bg-background p-2",
                                value: cap.capabilityId,
                                onChange: (e)=>setCap((c)=>({
                                            ...c,
                                            capabilityId: e.target.value
                                        })),
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "No registry capability"
                                    }),
                                    (capabilities.data?.items ?? []).map((c)=>/*#__PURE__*/ _jsx("option", {
                                            value: c.id,
                                            children: c.name
                                        }, c.id))
                                ]
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "Capability evidence",
                                testId: T.capEvidence,
                                value: cap.evidenceLabel,
                                onChange: (evidenceLabel)=>setCap((c)=>({
                                            ...c,
                                            evidenceLabel
                                        }))
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "Capability evidence reference",
                                testId: T.capEvidenceRef,
                                value: cap.evidenceRef,
                                onChange: (evidenceRef)=>setCap((c)=>({
                                            ...c,
                                            evidenceRef
                                        }))
                            }),
                            dialog === 'capability' && errorLine
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("h4", {
                    className: "font-semibold",
                    children: "Open gaps"
                }),
                gaps.length === 0 && /*#__PURE__*/ _jsxs("p", {
                    className: "text-sm",
                    children: [
                        "No open gaps yet. Add capability gaps in",
                        ' ',
                        /*#__PURE__*/ _jsx(Link, {
                            className: "underline",
                            to: pursuitSectionPath(captureProjectId, 'solution'),
                            children: "Solution"
                        }),
                        "."
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                    title: "Teaming candidates",
                    variant: "group",
                    action: !readOnly && hasCandidates ? /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>openDialog('candidate'),
                        "data-testid": Testids.pursuitTeaming.candidateAddButton,
                        children: "Add candidate"
                    }) : undefined
                }),
                hasCandidates ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(TextField, {
                            label: "Status note",
                            hint: "Needed when you change a candidate's status.",
                            testId: T.candStatusNote,
                            value: note,
                            onChange: setNote
                        }),
                        /*#__PURE__*/ _jsx("ul", {
                            className: "space-y-2",
                            children: (position.data?.candidates ?? []).map((c)=>/*#__PURE__*/ _jsx(CandidateRow, {
                                    captureProjectId: captureProjectId,
                                    cand: c,
                                    gapText: gapText,
                                    note: note,
                                    readOnly: readOnly,
                                    onStatus: (target, status)=>run(async ()=>{
                                            await projectDataClient.setCaptureTeamingCandidateStatus({
                                                captureProjectId,
                                                requestId: crypto.randomUUID(),
                                                candidateId: target.id,
                                                status,
                                                note
                                            });
                                            setNote('');
                                        })
                                }, c.id))
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    size: "compact",
                    title: "No teaming candidates yet.",
                    description: "Propose a company to fill an open gap.",
                    "data-testid": Testids.pursuitTeaming.candidateEmptyState,
                    action: readOnly ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>openDialog('candidate'),
                        "data-testid": Testids.pursuitTeaming.candidateAddButton,
                        children: "Add candidate"
                    })
                }),
                !readOnly && /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                    open: dialog === 'candidate',
                    onOpenChange: (open)=>setDialog(open ? 'candidate' : undefined),
                    title: "Propose teaming candidate",
                    description: "Propose a company for the gaps you tick.",
                    size: "lg",
                    testId: Testids.pursuitTeaming.candidateDialog,
                    footerProps: {
                        onCancel: ()=>setDialog(undefined),
                        confirmLabel: 'Propose teaming candidate',
                        onConfirm: ()=>run(async ()=>{
                                await projectDataClient.proposeCaptureTeamingCandidate({
                                    captureProjectId,
                                    requestId: crypto.randomUUID(),
                                    companyProfileId: companyId,
                                    rationale,
                                    theirCapability,
                                    gapIds
                                });
                                setCompanyId('');
                                setRationale('');
                                setTheirCapability('');
                                setGapIds([]);
                            }),
                        confirmProps: {
                            disabled: !companyId || !rationale.trim() || gapIds.length === 0,
                            'data-testid': T.candPropose
                        }
                    },
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-3",
                        children: [
                            gaps.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                className: "text-sm",
                                children: "No open gaps to propose against yet."
                            }) : /*#__PURE__*/ _jsx("ul", {
                                className: "space-y-2",
                                children: gaps.map((g)=>/*#__PURE__*/ _jsxs("li", {
                                        "data-testid": T.gapRow,
                                        className: "rounded-md border p-2 text-sm",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                                label: `${label(g.kind)} gap: ${g.description}`,
                                                controlProps: {
                                                    ...{
                                                        'data-testid': T.candGapOption
                                                    },
                                                    checked: gapIds.includes(g.id),
                                                    onCheckedChange: (checked)=>setGapIds((ids)=>checked === true ? [
                                                                ...ids,
                                                                g.id
                                                            ] : ids.filter((i)=>i !== g.id))
                                                }
                                            }),
                                            /*#__PURE__*/ _jsx("ul", {
                                                className: "pl-6 text-xs",
                                                children: g.evidence.map((e)=>/*#__PURE__*/ _jsxs("li", {
                                                        children: [
                                                            "Evidence — ",
                                                            label(e.sourceKind),
                                                            ": ",
                                                            e.sourceLabel,
                                                            e.excerpt && ` — ${e.excerpt}`
                                                        ]
                                                    }, e.id))
                                            })
                                        ]
                                    }, g.id))
                            }),
                            /*#__PURE__*/ _jsx(SharedCompanyPicker, {
                                value: companyId,
                                onSelect: setCompanyId
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "Why this company",
                                required: true,
                                multiline: true,
                                testId: T.candRationale,
                                value: rationale,
                                onChange: setRationale
                            }),
                            /*#__PURE__*/ _jsx(TextField, {
                                label: "What they bring",
                                multiline: true,
                                testId: T.candTheirCapability,
                                value: theirCapability,
                                onChange: setTheirCapability
                            }),
                            dialog === 'candidate' && errorLine
                        ]
                    })
                }),
                dialog === undefined && errorLine
            ]
        })
    });
}
